import student from "@assets/stage/funnel/student.webp"
import tutor from "@assets/stage/funnel/tutor.webp"
import sam from "@assets/stage/funnel/sam.webp"
import zoe from "@assets/stage/funnel/zoe.webp"
import laptop from "@assets/stage/funnel/laptop.webp"
import phone from "@assets/stage/funnel/phone.webp"
import tablet from "@assets/stage/funnel/tablet.webp"
import phoneLive from "@assets/stage/funnel/phone-live.webp"
import backpack from "@assets/stage/funnel/backpack.webp"
import book from "@assets/stage/funnel/book.webp"
import star from "@assets/stage/funnel/star.webp"
import path from "@assets/stage/funnel/path.webp"
import check from "@assets/stage/funnel/check.webp"
import confetti from "@assets/stage/funnel/confetti.webp"

/**
 * Les scènes du funnel stage, étape par étape.
 *
 * **Le principe : un agent n'apparaît que là où il agit.** Sam au compositeur —
 * c'est lui qui épaule le tuteur en séance ; Zoé à la réservation — c'est elle
 * qui alertera les parents. Partout ailleurs, un humain, ou un objet 3D qui dit
 * l'idée de l'écran. Quatre visages humains sur sept écrans, deux agents : le
 * parent achète du tutorat humain, augmenté — pas une armée de mascottes.
 *
 * Trois intensités :
 * - *signature* (compositeur, réservation) : des appareils qui montrent un VRAI
 *   écran de l'app — la séance live vue par l'élève sur portable, tablette et
 *   téléphone, puis un téléphone avec le portail parent — et l'agent qui les
 *   désigne. La séance est une capture réelle de la sandbox (un live créé,
 *   deux comptes connectés, la visio agrandie), retouchée d'un geste : le
 *   bouton « réduire » et la vignette de l'élève sont effacés (le mur
 *   derrière), parce qu'à cette taille ils se lisaient comme des taches — la
 *   barre d'appel est celle de l'app ; le portail vient de la landing page.
 *   La tablette et le téléphone recadrent l'écran du portable dans le même
 *   bezel. Rien de dessiné à la main, ni visage ni barre d'interface ;
 * - *humain + 3D* (classe, tuteurs, confirmation) : une silhouette, et deux ou
 *   trois objets 3D qui l'augmentent ;
 * - *objet 3D* (programme) : le résultat du travail de l'IA, sans l'IA.
 *
 * (Spécificités n'a pas de scène : une seule icône, servie par le bandeau
 * standard de `FunnelQuestionScreen`.)
 *
 * Les positions sont des fractions de la scène (0–1), par variante — le même
 * asset sert au bandeau mobile et au panneau desktop. `ratio` est celui du
 * fichier : la hauteur en découle, l'image n'est jamais déformée. Les champions, les
 * objets et les confettis flottent ; les humains et les appareils, non.
 */
export const FUNNEL_SCENES = {
    classe: {
        layers: [
            {
                source: student,
                ratio: 1.0127,
                mobile: { left: 0.12, bottom: -0.06, width: 0.46 },
                desktop: { left: 0.14, bottom: -0.03, width: 0.56 },
            },
            {
                source: backpack,
                ratio: 0.9663,
                float: true,
                rotate: 6,
                mobile: { left: 0.61, top: 0.1, width: 0.21 },
                desktop: { left: 0.64, bottom: 0.44, width: 0.24 },
            },
            {
                source: book,
                ratio: 1.4264,
                float: true,
                delay: 1200,
                rotate: -8,
                mobile: { left: 0.58, top: 0.58, width: 0.25 },
                desktop: { left: 0.6, bottom: 0.1, width: 0.28 },
            },
            {
                source: star,
                ratio: 1.0336,
                float: true,
                delay: 2000,
                rotate: -12,
                mobile: { left: 0.08, top: 0.08, width: 0.1 },
                desktop: { left: 0.1, bottom: 0.56, width: 0.11 },
            },
        ],
    },
    // Trois appareils, la même séance : des parents croient que Masteur ne
    // marche que sur ordinateur, et c'est à l'étape formule qu'ils se le
    // demandent. Le bandeau le dit en toutes lettres.
    composition: {
        layers: [
            {
                source: laptop,
                ratio: 1.3614,
                mobile: { left: 0.03, bottom: 0.1, width: 0.46 },
                desktop: { left: 0.03, bottom: 0.13, width: 0.6 },
            },
            {
                source: tablet,
                ratio: 1.3056,
                mobile: { left: 0.35, bottom: 0.06, width: 0.28 },
                desktop: { left: 0.43, bottom: 0.1, width: 0.31 },
            },
            {
                source: phoneLive,
                ratio: 0.5219,
                mobile: { left: 0.58, bottom: 0.04, width: 0.11 },
                desktop: { left: 0.66, bottom: 0.09, width: 0.115 },
            },
            {
                source: sam,
                ratio: 0.5761,
                float: true,
                mobile: { right: 0.04, bottom: 0.04, width: 0.2 },
                desktop: { right: 0.02, bottom: 0.1, width: 0.2 },
            },
        ],
        caption: [
            {
                icon: "laptop",
                title: "Sur ordinateur",
                subtitle: "Windows, Mac",
            },
            {
                icon: "tablet-alt",
                title: "Sur tablette",
                subtitle: "iPad, Android",
            },
            {
                icon: "mobile-alt",
                title: "Sur smartphone",
                subtitle: "iPhone, Android",
            },
        ],
    },
    programme: {
        layers: [
            {
                source: path,
                ratio: 1.4531,
                float: true,
                mobile: { left: 0.19, top: 0.12, width: 0.62 },
                desktop: { left: 0.1, bottom: 0.26, width: 0.8 },
            },
        ],
    },
    tuteurs: {
        layers: [
            {
                source: tutor,
                ratio: 0.7625,
                mobile: { left: 0.29, bottom: -0.08, width: 0.42 },
                desktop: { left: 0.2, bottom: -0.03, width: 0.56 },
            },
            {
                source: check,
                ratio: 1.0019,
                float: true,
                mobile: { left: 0.6, top: 0.56, width: 0.17 },
                desktop: { left: 0.62, bottom: 0.3, width: 0.18 },
            },
        ],
    },
    checkout: {
        layers: [
            {
                source: zoe,
                ratio: 0.5966,
                float: true,
                mobile: { left: 0.06, bottom: 0.02, width: 0.3 },
                desktop: { left: 0.08, bottom: 0.12, width: 0.36 },
            },
            {
                source: phone,
                ratio: 0.492,
                mobile: { right: 0.14, bottom: -0.22, width: 0.3 },
                desktop: { right: 0.14, bottom: 0.1, width: 0.36 },
            },
        ],
    },
    confirmation: {
        layers: [
            {
                source: confetti,
                ratio: 1.187,
                float: true,
                zIndex: 0,
                mobile: { left: 0.08, top: 0.0, width: 0.84 },
                desktop: { left: 0.06, bottom: 0.14, width: 0.88 },
            },
            {
                source: student,
                ratio: 1.0127,
                zIndex: 1,
                mobile: { left: 0.3, bottom: -0.06, width: 0.4 },
                desktop: { left: 0.22, bottom: -0.03, width: 0.54 },
            },
        ],
    },
}

/**
 * La scène de l'étape Tuteurs quand le matching a servi des visages : une
 * mosaïque de VRAIS masteurs — photos telles quelles, en cercles — et le
 * badge « vérifié » posé dessus. « Humain avant tout » : ce sont eux qui
 * accompagneront l'élève, pas un visage de landing page.
 *
 * Sept places au plus, tirées dans l'ordre servi (la vitrine d'abord) ;
 * `chosen` marque les visages des cartes, une fois la recherche terminée. Les
 * cadrages varient d'un tuteur à l'autre ; c'est voulu — ce sont leurs photos.
 */
const FACE_FRAMES = [
    {
        mobile: { left: 0.08, top: 0.12, width: 0.2 },
        desktop: { left: 0.12, bottom: 0.5, width: 0.22 },
    },
    {
        mobile: { left: 0.33, top: 0.04, width: 0.24 },
        desktop: { left: 0.4, bottom: 0.58, width: 0.26 },
    },
    {
        mobile: { left: 0.62, top: 0.14, width: 0.2 },
        desktop: { left: 0.68, bottom: 0.48, width: 0.22 },
    },
    {
        mobile: { left: 0.16, top: 0.52, width: 0.18 },
        desktop: { left: 0.22, bottom: 0.16, width: 0.2 },
    },
    {
        mobile: { left: 0.42, top: 0.56, width: 0.2 },
        desktop: { left: 0.5, bottom: 0.1, width: 0.24 },
    },
    {
        mobile: { left: 0.7, top: 0.5, width: 0.16 },
        desktop: { left: 0.78, bottom: 0.18, width: 0.18 },
    },
    {
        mobile: { left: 0.86, top: 0.3, width: 0.12 },
        desktop: { left: 0.06, bottom: 0.24, width: 0.14 },
    },
]

export const tutorFacesScene = (avatars, chosen = [], skillSetByUri = {}) => ({
    layers: [
        ...avatars.slice(0, FACE_FRAMES.length).map((uri, i) => ({
            key: uri,
            source: { uri },
            ratio: 1,
            round: true,
            tie: true,
            // Les retenus (les cartes) se distinguent une fois la recherche
            // finie : cerclés de la couleur de la marque, devant les autres.
            chosen: chosen.includes(uri),
            // Le champion du masteur (couleur d'anneau et nœud de sa carte)
            // quand le matching le sert ; sinon le défaut de `AvatarM`.
            skillSetId: skillSetByUri[uri],
            float: i % 2 === 0,
            delay: i * 260,
            zIndex: 1,
            ...FACE_FRAMES[i],
        })),
        {
            key: "check",
            source: check,
            ratio: 1.0019,
            float: true,
            zIndex: 2,
            mobile: { left: 0.52, top: 0.34, width: 0.15 },
            desktop: { left: 0.57, bottom: 0.4, width: 0.16 },
        },
    ],
})
